export type AdminLocale = 'en' | 'th';

/**
 * Admin UI translation dictionary.
 * English strings come from the component defaults (fallbacks).
 * Only Thai needs explicit entries here.
 */
const th: Record<string, string> = {
  // ── Navigation ──────────────────────────────────────────────────────────────
  'nav.dashboard': 'แดชบอร์ด',
  'nav.content': 'เนื้อหา',
  'nav.media': 'สื่อ',
  'nav.users': 'ผู้ใช้งาน',
  'nav.settings': 'การตั้งค่า',
  'nav.trash': 'ถังขยะ',
  'nav.activity': 'กิจกรรม',
  'nav.errors': 'ข้อผิดพลาด',
  'nav.scheduler': 'ตัวกำหนดเวลา',
  'nav.email': 'อีเมล',
  'nav.plugins': 'ปลั๊กอิน',
  'nav.taxonomies': 'หมวดหมู่',
  'nav.public-users': 'ผู้ใช้สาธารณะ',
  'nav.post-types': 'ประเภทเนื้อหา',
  'nav.menu': 'เมนู',
  'nav.profile': 'โปรไฟล์',

  // ── Common actions ───────────────────────────────────────────────────────────
  'action.save': 'บันทึก',
  'action.saving': 'กำลังบันทึก...',
  'action.cancel': 'ยกเลิก',
  'action.close': 'ปิด',
  'action.delete': 'ลบ',
  'action.edit': 'แก้ไข',
  'action.create': 'สร้าง',
  'action.add': 'เพิ่ม',
  'action.remove': 'ลบออก',
  'action.back': 'กลับ',
  'action.next': 'ถัดไป',
  'action.confirm': 'ยืนยัน',
  'action.publish': 'เผยแพร่',
  'action.unpublish': 'ยกเลิกเผยแพร่',
  'action.preview': 'ดูตัวอย่าง',
  'action.search': 'ค้นหา',
  'action.filter': 'กรอง',
  'action.reset': 'รีเซ็ต',
  'action.export': 'ส่งออก',
  'action.import': 'นำเข้า',
  'action.restore': 'กู้คืน',
  'action.view': 'ดู',
  'action.copy': 'คัดลอก',
  'action.generate': 'สร้างอัตโนมัติ',
  'action.signout': 'ออกจากระบบ',
  'action.signing-out': 'กำลังออกจากระบบ...',
  'action.process': 'ดำเนินการ',
  'action.pause': 'หยุดชั่วคราว',
  'action.resume': 'ดำเนินการต่อ',
  'action.retry': 'ลองใหม่',
  'action.purge': 'ล้างข้อมูล',

  // ── Common labels ────────────────────────────────────────────────────────────
  'label.title': 'ชื่อเรื่อง',
  'label.slug': 'สลัก (URL)',
  'label.status': 'สถานะ',
  'label.author': 'ผู้เขียน',
  'label.date': 'วันที่',
  'label.type': 'ประเภท',
  'label.name': 'ชื่อ',
  'label.email': 'อีเมล',
  'label.role': 'บทบาท',
  'label.actions': 'การดำเนินการ',
  'label.loading': 'กำลังโหลด...',
  'label.search': 'ค้นหา...',
  'label.no-results': 'ไม่พบผลลัพธ์',
  'label.select-all': 'เลือกทั้งหมด',
  'label.selected': 'รายการที่เลือก',
  'label.optional': 'ไม่บังคับ',
  'label.required': 'จำเป็น',
  'label.description': 'คำอธิบาย',
  'label.created': 'สร้างเมื่อ',
  'label.updated': 'อัปเดตเมื่อ',
  'label.published': 'เผยแพร่เมื่อ',
  'label.password': 'รหัสผ่าน',
  'label.new-password': 'รหัสผ่านใหม่',
  'label.confirm-password': 'ยืนยันรหัสผ่าน',
  'label.language': 'ภาษา',
  'label.timezone': 'เขตเวลา',
  'label.time-format': 'รูปแบบเวลา',
  'label.date-format': 'รูปแบบวันที่',
  'label.notes': 'หมายเหตุ',
  'label.enabled': 'เปิดใช้งาน',
  'label.icon': 'ไอคอน',
  'label.parent': 'หัวข้อแม่',
  'label.sort-order': 'ลำดับการเรียง',
  'label.version': 'เวอร์ชัน',
  'label.source': 'แหล่งที่มา',
  'label.installed': 'ติดตั้งเมื่อ',
  'label.permissions': 'สิทธิ์',
  'label.error-count': 'จำนวนข้อผิดพลาด',
  'label.group': 'กลุ่ม',
  'label.priority': 'ความสำคัญ',
  'label.max-retries': 'ลองใหม่สูงสุด',
  'label.hook': 'ฮุค',
  'label.attempts': 'จำนวนครั้งที่ลอง',
  'label.scheduled': 'กำหนดเวลา',
  'label.last-log': 'บันทึกล่าสุด',
  'label.ip': 'ที่อยู่ IP',
  'label.user': 'ผู้ใช้',
  'label.details': 'รายละเอียด',
  'label.time': 'เวลา',
  'label.model': 'โมเดล',
  'label.quality': 'คุณภาพ',
  'label.format': 'รูปแบบ',
  'label.recipient-email': 'อีเมลผู้รับ',
  'label.provider-name': 'ชื่อผู้ให้บริการ',
  'label.from-email': 'อีเมลผู้ส่ง',
  'label.host': 'โฮสต์',
  'label.port': 'พอร์ต',
  'label.username': 'ชื่อผู้ใช้',
  'label.encryption': 'การเข้ารหัส',
  'label.client-id': 'Client ID',
  'label.sending-domain': 'โดเมนส่งอีเมล',
  'label.template-name': 'ชื่อเทมเพลต',
  'label.subject-line': 'หัวข้ออีเมล',
  'label.display-type': 'ประเภทการแสดงผล',
  'label.required-capability': 'สิทธิ์ที่ต้องการ',
  'label.visible-in-sidebar': 'แสดงในแถบด้านข้าง',
  'label.base-slug': 'สลักฐาน',
  'label.allowed-fields': 'ฟิลด์ที่อนุญาต',
  'label.schedule-type': 'ประเภทกำหนดเวลา',
  'label.batch-size': 'ขนาดชุด',
  'label.font-family': 'แบบอักษร',
  'label.border-radius': 'ความโค้งมุม',
  'label.spacing-density': 'ความหนาแน่นช่องว่าง',
  'label.number-of-revisions': 'จำนวนเวอร์ชัน',
  'label.site-description': 'คำอธิบายเว็บไซต์',
  'label.payload-url': 'URL ปลายทาง',
  'label.events': 'เหตุการณ์',
  'label.select-language': 'เลือกภาษา',
  'label.cache-duration': 'ระยะเวลาแคช',
  'label.presets': 'ค่าตั้งต้น',

  // ── Status badges ────────────────────────────────────────────────────────────
  'status.published': 'เผยแพร่แล้ว',
  'status.draft': 'แบบร่าง',
  'status.archived': 'เก็บถาวร',
  'status.pending': 'รอดำเนินการ',
  'status.scheduled': 'กำหนดเวลา',
  'status.active': 'ใช้งาน',
  'status.inactive': 'ไม่ใช้งาน',
  'status.paused': 'หยุดชั่วคราว',
  'status.trashed': 'ถังขยะ',
  'status.banned': 'ถูกแบน',
  'status.running': 'กำลังทำงาน',
  'status.complete': 'เสร็จสิ้น',
  'status.failed': 'ล้มเหลว',
  'status.canceled': 'ยกเลิก',
  'status.sent': 'ส่งแล้ว',
  'status.bounced': 'ตีกลับ',
  'status.processing': 'กำลังประมวลผล',
  'status.completed': 'เสร็จสมบูรณ์',

  // ── Activity action labels ────────────────────────────────────────────────────
  'activity.action.create': 'สร้าง',
  'activity.action.update': 'อัปเดต',
  'activity.action.delete': 'ลบ',
  'activity.action.publish': 'เผยแพร่',
  'activity.action.unpublish': 'ยกเลิกเผยแพร่',
  'activity.action.login': 'เข้าสู่ระบบ',
  'activity.action.logout': 'ออกจากระบบ',
  'activity.action.upload': 'อัปโหลด',
  'activity.action.approve': 'อนุมัติ',
  'activity.action.spam': 'สแปม',
  'activity.action.restore': 'กู้คืน',
  'activity.action.purge': 'ล้างข้อมูล',
  'activity.action.clone': 'โคลน',
  'activity.action.export': 'ส่งออก',
  'activity.action.erase': 'ลบถาวร',
  'activity.action.bulk-action': 'การดำเนินการกลุ่ม',
  'activity.action.settings': 'การตั้งค่า',
  'activity.action.reorder': 'เรียงลำดับใหม่',

  // ── Activity entity type labels ───────────────────────────────────────────────
  'activity.entity.post-type': 'ประเภทเนื้อหา',
  'activity.entity.taxonomy': 'หมวดหมู่',
  'activity.entity.term': 'หัวข้อ',
  'activity.entity.session': 'เซสชัน',
  'activity.entity.plugin': 'ปลั๊กอิน',
  'activity.entity.privacy-request': 'คำขอความเป็นส่วนตัว',
  'activity.entity.menu-item': 'รายการเมนู',
  'activity.entity.locale': 'ภาษา',
  'activity.entity.email-provider': 'ผู้ให้บริการอีเมล',
  'activity.entity.role': 'บทบาท',

  // ── Activity columns & summary ────────────────────────────────────────────────
  'activity.col.action': 'การดำเนินการ',
  'activity.col.entity-type': 'ประเภทรายการ',
  'activity.col.user': 'ผู้ใช้',
  'activity.col.details': 'รายละเอียด',
  'activity.col.ip': 'ที่อยู่ IP',
  'activity.col.time': 'เวลา',
  'activity.bulk.purge-90': 'ล้างบันทึกที่เก่ากว่า 90 วัน',
  'activity.summary.total': 'กิจกรรมทั้งหมด',
  'activity.summary.top-actions': 'การดำเนินการยอดนิยม',
  'activity.summary.daily-trend': 'แนวโน้มรายวัน',
  'activity.summary.most-active': 'ผู้ใช้งานบ่อยสุด',
  'activity.summary.last-7-days': '7 วันที่ผ่านมา',
  'activity.dialog.change-details': 'รายละเอียดการเปลี่ยนแปลง',

  // ── Auth events ───────────────────────────────────────────────────────────────
  'auth-event.login-success': 'เข้าสู่ระบบสำเร็จ',
  'auth-event.login-failed': 'เข้าสู่ระบบล้มเหลว',
  'auth-event.logout': 'ออกจากระบบ',
  'auth-event.password-changed': 'เปลี่ยนรหัสผ่านแล้ว',
  'auth-event.password-reset': 'รีเซ็ตรหัสผ่านแล้ว',
  'auth-event.session-revoked': 'เพิกถอนเซสชันแล้ว',
  'auth-event.account-locked': 'บัญชีถูกล็อก',
  'auth-event.col.event-type': 'ประเภทเหตุการณ์',
  'auth-event.filter.all': 'ทุกเหตุการณ์',

  // ── Scheduler ────────────────────────────────────────────────────────────────
  'scheduler.status.pending': 'รอดำเนินการ',
  'scheduler.status.running': 'กำลังทำงาน',
  'scheduler.status.complete': 'เสร็จสิ้น',
  'scheduler.status.failed': 'ล้มเหลว',
  'scheduler.status.canceled': 'ยกเลิก',
  'scheduler.status.paused': 'หยุดชั่วคราว',
  'scheduler.col.status': 'สถานะ',
  'scheduler.col.hook': 'ฮุค',
  'scheduler.col.type': 'ประเภท',
  'scheduler.col.group': 'กลุ่ม',
  'scheduler.col.attempts': 'จำนวนครั้ง',
  'scheduler.col.scheduled': 'กำหนดเวลา',
  'scheduler.col.last-log': 'บันทึกล่าสุด',
  'scheduler.bulk.pause': 'หยุดที่เลือก',
  'scheduler.bulk.resume': 'ดำเนินการต่อที่เลือก',
  'scheduler.bulk.retry': 'ลองใหม่ที่เลือก',
  'scheduler.bulk.cancel': 'ยกเลิกที่เลือก',
  'scheduler.config.trigger': 'ทริกเกอร์',
  'scheduler.config.built-in-trigger': 'ทริกเกอร์ในตัว',
  'scheduler.config.performance': 'ประสิทธิภาพ',
  'scheduler.config.batch-size': 'ขนาดชุด',
  'scheduler.breadcrumb.dashboard': 'แดชบอร์ด',
  'scheduler.breadcrumb.scheduler': 'ตัวกำหนดเวลา',
  'scheduler.breadcrumb.config': 'ตั้งค่า',

  // ── Plugins ───────────────────────────────────────────────────────────────────
  'plugin.tab.data': 'ข้อมูล',
  'plugin.tab.settings': 'การตั้งค่า',
  'plugin.tab.info': 'ข้อมูลปลั๊กอิน',
  'plugin.info.configuration': 'การกำหนดค่า',
  'plugin.info.enabled': 'เปิดใช้งาน',
  'plugin.info.name': 'ชื่อ',
  'plugin.info.description': 'คำอธิบาย',
  'plugin.info.author': 'ผู้พัฒนา',
  'plugin.info.slug': 'สลัก',
  'plugin.info.version': 'เวอร์ชัน',
  'plugin.info.source': 'แหล่งที่มา',
  'plugin.info.installed': 'ติดตั้งเมื่อ',
  'plugin.info.permissions': 'สิทธิ์',
  'plugin.info.error-count': 'จำนวนข้อผิดพลาด',

  // ── Privacy ───────────────────────────────────────────────────────────────────
  'privacy.request.data-export': 'ส่งออกข้อมูล',
  'privacy.request.data-erasure': 'ลบข้อมูล',

  // ── Email logs ────────────────────────────────────────────────────────────────
  'email-logs.custom': 'กำหนดเอง',
  'email-logs.dialog.title': 'รายละเอียดอีเมล',

  // ── Email providers ───────────────────────────────────────────────────────────
  'email-provider.action.add': 'เพิ่มผู้ให้บริการ',
  'email-provider.dialog.edit': 'แก้ไขผู้ให้บริการอีเมล',
  'email-provider.dialog.test': 'ส่งอีเมลทดสอบ',

  // ── Email templates ───────────────────────────────────────────────────────────
  'email-template.action.add': 'เพิ่มเทมเพลต',

  // ── Menu editor ───────────────────────────────────────────────────────────────
  'menu-editor.action.add-item': 'เพิ่มรายการ',
  'menu-editor.dialog.new-item': 'รายการเมนูใหม่',

  // ── Locales ───────────────────────────────────────────────────────────────────
  'locales.dialog.add': 'เพิ่มภาษา',

  // ── Page headers ──────────────────────────────────────────────────────────────
  'page.dashboard': 'แดชบอร์ด',
  'page.media': 'คลังสื่อ',
  'page.users': 'ผู้ใช้งาน',
  'page.settings': 'การตั้งค่า',
  'page.trash': 'ถังขยะ',
  'page.profile': 'โปรไฟล์ของฉัน',
  'page.activity': 'บันทึกกิจกรรม',
  'page.scheduler': 'ตัวกำหนดเวลา',
  'page.errors': 'บันทึกข้อผิดพลาด',
  'page.user-management': 'การจัดการผู้ใช้',
  'page.public-users': 'ผู้ใช้สาธารณะ',
  'page.site-options': 'ตัวเลือกเว็บไซต์',
  'page.plugins': 'ปลั๊กอิน',
  'page.email-logs': 'บันทึกอีเมล',
  'page.email-providers': 'ผู้ให้บริการอีเมล',
  'page.email-templates': 'เทมเพลตอีเมล',
  'page.menu-items': 'รายการเมนู',
  'page.custom-fields': 'ฟิลด์กำหนดเอง',

  // ── Page subtitles ────────────────────────────────────────────────────────────
  'subtitle.user-management':
    'จัดการผู้ดูแลระบบ ผู้ใช้สาธารณะ และสิทธิ์การเข้าถึง',
  'subtitle.public-users': 'จัดการผู้ใช้หน้าเว็บไซต์',
  'subtitle.profile': 'จัดการข้อมูลบัญชีของคุณ',
  'subtitle.field-groups': 'กำหนดฟิลด์เมตาดาต้าและกำหนดให้กับประเภทเนื้อหา',
  'subtitle.site-options': 'จัดการตัวเลือกทั่วทั้งเว็บไซต์และข้อมูลชั่วคราว',
  'subtitle.settings': 'จัดการการตั้งค่าและการปรับแต่งเว็บไซต์',
  'subtitle.email-providers': 'กำหนดค่าบริการส่งอีเมล',
  'subtitle.email-logs': 'ดูและจัดการประวัติอีเมลที่ส่งแล้ว',
  'subtitle.email-templates': 'ปรับแต่งเนื้อหาอีเมลสำหรับการแจ้งเตือน',
  'subtitle.menu-items': 'ปรับแต่งการนำทางแถบด้านข้าง',

  // ── Tabs: Users ───────────────────────────────────────────────────────────────
  'users.tab.admin-users': 'ผู้ดูแลระบบ',
  'users.tab.public-users': 'ผู้ใช้สาธารณะ',
  'users.tab.roles': 'บทบาท',

  // ── Settings sections ────────────────────────────────────────────────────────
  'settings.general': 'ทั่วไป',
  'settings.general.description': 'การตั้งค่าเว็บไซต์พื้นฐาน',
  'settings.datetime': 'วันที่และเวลา',
  'settings.content': 'เนื้อหา',
  'settings.content.description': 'กำหนดพฤติกรรมการแก้ไขเนื้อหา',
  'settings.features': 'ฟีเจอร์',

  // ── Appearance settings ───────────────────────────────────────────────────────
  'appearance.title': 'การตั้งค่าหน้าตา',
  'appearance.branding': 'แบรนด์',
  'appearance.theme-color': 'สีธีม',
  'appearance.typography': 'ตัวอักษรและ UI',

  // ── MCP settings ─────────────────────────────────────────────────────────────
  'mcp.connection-details': 'รายละเอียดการเชื่อมต่อ',
  'mcp.available-resources': 'ทรัพยากรที่ใช้งานได้',

  // ── Post types ────────────────────────────────────────────────────────────────
  'post-type.tab.general': 'ทั่วไป',
  'post-type.tab.field-groups': 'กลุ่มฟิลด์',
  'post-type.tab.display': 'การแสดงผล',
  'post-type.field.menu-position': 'ตำแหน่งเมนู',
  'post-type.field.features': 'ฟีเจอร์',
  'post-type.field.taxonomies': 'หมวดหมู่',
  'post-type.access.public': 'สาธารณะ',
  'post-type.access.authenticated': 'ต้องยืนยันตัวตน',

  // ── Field groups ──────────────────────────────────────────────────────────────
  'fg.new-name': 'กลุ่มฟิลด์ใหม่',
  'fg.failed-create': 'ไม่สามารถสร้างกลุ่มได้',
  'fg.failed-update-status': 'ไม่สามารถอัปเดตสถานะได้',
  'fg.moved-to-trash': '"{name}" ถูกย้ายไปถังขยะแล้ว',
  'fg.failed-move-to-trash': 'ไม่สามารถย้ายไปถังขยะได้',
  'fg.restored': '"{name}" ถูกกู้คืนแล้ว',
  'fg.failed-restore': 'ไม่สามารถกู้คืนได้',
  'fg.permanently-deleted': '"{name}" ถูกลบถาวรแล้ว',
  'fg.failed-delete': 'ไม่สามารถลบได้',
  'fg.col.name': 'ชื่อ',
  'fg.col.assigned-to': 'กำหนดให้',
  'fg.col.fields': 'ฟิลด์',
  'fg.col.active': 'เปิดใช้งาน',
  'fg.scope.shared': 'ใช้ร่วมกัน',
  'fg.scope.per-locale': 'แยกตามภาษา',
  'fg.unassigned': 'ยังไม่ได้กำหนด',
  'fg.action.edit': 'แก้ไข',
  'fg.action.enable': 'เปิดใช้งาน',
  'fg.action.disable': 'ปิดใช้งาน',
  'fg.action.move-to-trash': 'ย้ายไปถังขยะ',
  'fg.action.restore': 'กู้คืน',
  'fg.action.delete-permanently': 'ลบถาวร',
  'fg.tab.all': 'ทั้งหมด',
  'fg.tab.shared': 'ใช้ร่วมกัน',
  'fg.tab.per-locale': 'แยกตามภาษา',
  'fg.tab.trash': 'ถังขยะ',
  'fg.add-field-group': 'เพิ่มกลุ่มฟิลด์',
  'fg.search-placeholder': 'ค้นหากลุ่มฟิลด์…',
  'fg.empty.trash': 'ไม่มีกลุ่มในถังขยะ',
  'fg.empty.default': 'ยังไม่มีกลุ่มฟิลด์',
  'fg.confirm.trash-title': 'ย้ายไปถังขยะ',
  'fg.confirm.trash-desc':
    '"{name}" จะถูกย้ายไปถังขยะ คุณสามารถกู้คืนได้ภายหลัง',
  'fg.confirm.trash-button': 'ย้ายไปถังขยะ',
  'fg.confirm.delete-title': 'ลบถาวร',
  'fg.confirm.delete-desc':
    '"{name}" และฟิลด์ทั้งหมดจะถูกลบถาวร ไม่สามารถย้อนกลับได้',
  'fg.confirm.delete-button': 'ลบถาวร',
  'fg.field-count': '{count} ฟิลด์',
  'fg.edit-layout': 'แก้ไขเลย์เอาท์',
  'fg.remove-tooltip': 'ลบออกจากประเภทเนื้อหานี้',
  'fg.remove-tooltip-detail':
    'ข้อมูลที่มีอยู่จะถูกเก็บรักษาไว้ และจะกลับมาเมื่อกำหนดใหม่',
  'fg.count-assigned': '{count} กำหนดแล้ว',
  'fg.none-assigned': 'ยังไม่ได้กำหนด',
  'fg.add-placeholder': 'เพิ่มกลุ่มฟิลด์…',
  'fg.create-new': '+ สร้างกลุ่มฟิลด์ใหม่',
  'fg.section.all': 'กลุ่มฟิลด์',
  'fg.section.shared': 'กลุ่มฟิลด์ใช้ร่วมกัน',
  'fg.section.per-locale': 'กลุ่มฟิลด์แยกตามภาษา',
  'fg.loading': 'กำลังโหลดกลุ่มฟิลด์...',

  // ── Post type form ───────────────────────────────────────────────────────────
  'post-type.field.name-singular': 'ชื่อ (เอกพจน์)',
  'post-type.field.name-plural': 'ชื่อ (พหูพจน์)',
  'post-type.field.slug': 'slug',
  'post-type.field.slug-placeholder': 'สร้างอัตโนมัติ',
  'post-type.field.description-placeholder': 'อธิบายประเภทเนื้อหานี้',
  'post-type.feature.excerpt': 'บทคัดย่อ',
  'post-type.feature.revisions': 'การแก้ไข',
  'post-type.feature.hierarchical': 'ลำดับชั้น',
  'post-type.feature.show-in-menu': 'แสดงในเมนู',
  'post-type.feature.seo': 'ฟิลด์ SEO',
  'post-type.list-columns': 'คอลัมน์รายการ',
  'post-type.list-columns-desc': 'กำหนดคอลัมน์ที่จะแสดงในตารางรายการเนื้อหา',
  'post-type.custom-field-badge': 'ฟิลด์กำหนดเอง',
  'post-type.col-key-placeholder': 'คีย์คอลัมน์ (เช่น field:my_field)',
  'post-type.col-label-placeholder': 'ป้ายชื่อ',
  'post-type.save-changes': 'บันทึกการเปลี่ยนแปลง',
  'post-type.create': 'สร้างประเภทเนื้อหา',

  // ── Taxonomy ──────────────────────────────────────────────────────────────────
  'taxonomy.tab.general': 'ทั่วไป',
  'taxonomy.tab.fields': 'ฟิลด์',
  'taxonomy.tab.options': 'ตัวเลือก',
  'taxonomy.field.none': 'ไม่มี',

  // ── Storage settings ──────────────────────────────────────────────────────────
  'storage.file-organization': 'การจัดระเบียบไฟล์',
  'storage.conflict.keep-original': 'เก็บต้นฉบับ',
  'storage.conflict.sanitize-names': 'ล้างชื่อไฟล์',
  'storage.conflict.generate-uuid': 'สร้าง UUID',
  'storage.conflict.auto-rename': 'เปลี่ยนชื่ออัตโนมัติ',
  'storage.conflict.overwrite': 'เขียนทับ',
  'storage.conflict.reject': 'ปฏิเสธ',
  'storage.upload-restrictions': 'ข้อจำกัดการอัปโหลด',
  'storage.image-optimization': 'การปรับภาพ',
  'storage.quality.smaller': 'เล็กลง',
  'storage.quality.higher': 'คุณภาพสูงขึ้น',
  'storage.format.keep-original': 'เก็บรูปแบบเดิม',
  'storage.format.webp': 'WebP',

  // ── AI settings ───────────────────────────────────────────────────────────────
  'ai.openrouter-api': 'OpenRouter API',

  // ── API settings ──────────────────────────────────────────────────────────────
  'api.access.public': 'สาธารณะ',
  'api.access.authenticated': 'ต้องยืนยันตัวตน',

  // ── Sidebar ───────────────────────────────────────────────────────────────────
  'sidebar.tooltip.settings': 'การตั้งค่า',
  'sidebar.tooltip.sign-out': 'ออกจากระบบ',
  'sidebar.default.dashboard': 'แดชบอร์ด',
  'sidebar.default.posts': 'โพสต์',
  'sidebar.default.media': 'สื่อ',
  'sidebar.default.users': 'ผู้ใช้งาน',
  'sidebar.default.activity': 'กิจกรรม',
  'sidebar.default.settings': 'การตั้งค่า',

  // ── Auth / Login ──────────────────────────────────────────────────────────────
  'auth.label.email': 'อีเมล',
  'auth.label.password': 'รหัสผ่าน',
  'auth.label.new-password': 'รหัสผ่านใหม่',
  'auth.label.confirm-password': 'ยืนยันรหัสผ่าน',
  'auth.verify.title': 'ยืนยันตัวตน',

  // ── Setup wizard ──────────────────────────────────────────────────────────────
  'setup.title': 'ตัวช่วยติดตั้ง',
  'setup.datetime': 'วันที่และเวลา',
  'setup.label.language': 'ภาษา',
  'setup.label.confirm-password': 'ยืนยันรหัสผ่าน',
  'setup.label.time-format': 'รูปแบบเวลา',
  'setup.label.date-format': 'รูปแบบวันที่',
  'setup.label.timezone': 'เขตเวลา',

  // ── Profile menu ─────────────────────────────────────────────────────────────
  'menu.my-profile': 'โปรไฟล์ของฉัน',
  'menu.sign-out': 'ออกจากระบบ',
  'menu.language': 'ภาษา',
  'menu.dark-mode': 'โหมดมืด',
  'menu.light-mode': 'โหมดสว่าง',
  'menu.switch-to-light': 'เปลี่ยนเป็นโหมดสว่าง',
  'menu.switch-to-dark': 'เปลี่ยนเป็นโหมดมืด',

  // ── Sign-out dialog ──────────────────────────────────────────────────────────
  'dialog.sign-out.title': 'ออกจากระบบ?',
  'dialog.sign-out.description': 'คุณจะถูกนำไปยังหน้าเข้าสู่ระบบ',
  'dialog.sign-out.confirm': 'ออกจากระบบ',

  // ── Profile menu: theme ────────────────────────────────────────────────────
  'menu.personal-theme': 'ธีมส่วนตัว',
  'menu.use-global-theme': 'ใช้ธีมทั่วไป',

  // ── Post type: preview ────────────────────────────────────────────────────
  'post-type.feature.preview': 'ดูตัวอย่าง',
  'post-type.field.preview-path': 'เทมเพลตพาธดูตัวอย่าง',

  // ── Field groups: inactive ────────────────────────────────────────────────
  'fg.inactive': 'ไม่ใช้งาน',

  // ── Settings: preview base URL ────────────────────────────────────────────
  'label.preview-base-url': 'URL ฐานดูตัวอย่าง',

  // ── Data table ────────────────────────────────────────────────────────────
  'table.item': 'รายการ',
  'table.items': 'รายการ',
  'table.filtered': 'กรองแล้ว',
  'table.selected': 'เลือกแล้ว',
  'table.showing': 'แสดง',
  'table.of': 'จาก',
  'table.per-page': 'ต่อหน้า',
  'table.previous-page': 'หน้าก่อน',
  'table.next-page': 'หน้าถัดไป',
  'table.page': 'หน้า',
  'table.all-items-selected': 'เลือกทั้งหมด {total} รายการแล้ว',
  'table.all-page-selected': 'เลือกทั้งหมด {count} รายการในหน้านี้แล้ว',
  'table.select-all-items': 'เลือกทั้งหมด {total} รายการ',
  'table.clear-selection': 'ยกเลิกการเลือก',
  'table.export-csv': 'ส่งออก CSV',
  'table.export-select-columns': 'เลือกคอลัมน์ที่จะส่งออก ({count} แถว)',
  'table.select-all': 'เลือกทั้งหมด',
  'table.deselect-all': 'ยกเลิกเลือกทั้งหมด',
  'table.hidden': 'ซ่อน',
  'table.export-columns': 'ส่งออก {count} คอลัมน์',

  // ── Locale toggle ────────────────────────────────────────────────────────────
  'locale.en': 'EN',
  'locale.th': 'TH',
};

export const adminTranslations: Record<AdminLocale, Record<string, string>> = {
  en: {},
  th,
};
